import { FunctionComponent, useRef, useState, useEffect } from "react";
import cx from "classnames";

import Checker, { CheckerStatus } from "./Checker";
import { Color, Move, Player, PointState } from "./Types";
import { Animation } from "./Animations";
import { LastPointClicked } from "./store/lastPointClickedSlice";

export enum BoardPointLocation {
  Top = "TOP",
  Bottom = "BOTTOM",
}

export type BoardPointProps = {
  pointState: PointState;
  location: BoardPointLocation;
  playerOneColor: Color;
  playerTwoColor: Color;
  pointNumber: number;
  clickHandler: (fromPoint: number | "BAR") => boolean;
  allPossibleInitialMoves: Move[];
  allPossibleMoveSetsFromSelectedPoint: Move[][];
  lastPointClicked: LastPointClicked;
  currAnimations: Animation[];
  onAnimationComplete: (id: number) => void;
  checkerPulse: boolean;
};

const BoardPoint: FunctionComponent<BoardPointProps> = ({
  pointState,
  location,
  playerOneColor,
  playerTwoColor,
  pointNumber,
  clickHandler,
  allPossibleInitialMoves,
  allPossibleMoveSetsFromSelectedPoint,
  lastPointClicked,
  currAnimations,
  onAnimationComplete,
  checkerPulse,
}: BoardPointProps) => {
  if (pointState[Player.One] > 0 && pointState[Player.Two] > 0) {
    console.error("Invalid PointState on point " + pointNumber);
    console.error(pointState);
  }
  const [showNoMoveHighlight, setShowNoMoveHighlight] = useState(false);

  const topOrBottomClass = {
    top: location === BoardPointLocation.Top,
    bottom: location === BoardPointLocation.Bottom,
  };

  let highlight = null;
  const isHighlightedToPoint =
    lastPointClicked.point !== -1 &&
    allPossibleMoveSetsFromSelectedPoint.some(
      (moveSet: Move[]) =>
        moveSet[moveSet.length - 1].to === pointNumber &&
        moveSet[0].from === lastPointClicked.point
    );
  if (isHighlightedToPoint || showNoMoveHighlight) {
    highlight = (
      <div
        className={cx("Point-wrapper-highlight", {
          ...topOrBottomClass,
          to: isHighlightedToPoint,
          noMove: showNoMoveHighlight,
        })}
      />
    );
  }

  const isSelectedFromPoint = lastPointClicked.point === pointNumber;
  const isHighlightedFromPoint =
    lastPointClicked.point === -1 &&
    allPossibleInitialMoves.some((move: Move) => move.from === pointNumber);

  const occupyingPlayer =
    pointState[Player.One] > pointState[Player.Two] ? Player.One : Player.Two;
  const checkerCount =
    pointState[Player.One] > pointState[Player.Two]
      ? pointState[Player.One]
      : pointState[Player.Two];

  // Рассчитываем перекрытие: CSS-переменная --overlap на обёртке,
  // а margin задаётся через CSS (не inline на шашке — чтобы не ломать анимации).
  const wrapperRef = useRef<HTMLDivElement>(null);
  const [overlapPx, setOverlapPx] = useState(0);

  useEffect(() => {
    const recalc = () => {
      if (!wrapperRef.current || checkerCount <= 1) {
        setOverlapPx(0);
        return;
      }
      const wrapper = wrapperRef.current;
      const wrapperHeight = wrapper.clientHeight;
      const firstChecker = wrapper.querySelector(".Checker") as HTMLElement;
      if (!firstChecker) { setOverlapPx(0); return; }
      const checkerHeight = firstChecker.offsetHeight;
      const totalNeeded = checkerCount * checkerHeight;
      if (totalNeeded > wrapperHeight && checkerCount > 1) {
        setOverlapPx((totalNeeded - wrapperHeight) / (checkerCount - 1));
      } else {
        setOverlapPx(0);
      }
    };
    recalc();
    window.addEventListener("resize", recalc);
    return () => window.removeEventListener("resize", recalc);
  }, [checkerCount]);

  const checkers = [];
  for (let i = 0; i < checkerCount; i++) {
    const color =
      occupyingPlayer === Player.One ? playerOneColor : playerTwoColor;

    let anim = currAnimations.find((a) => {
      return a.location === pointNumber && a.checkerNumber === i + 1;
    });

    let status = CheckerStatus.None;
    if (i === checkerCount - 1) {
      if (isSelectedFromPoint) {
        status = CheckerStatus.Selected;
      } else if (isHighlightedFromPoint) {
        status = CheckerStatus.Highlighted;
      }
    }

    checkers.push(
      <Checker
        key={i + color}
        color={color}
        checkerPulse={checkerPulse}
        status={status}
        animation={anim}
        onAnimationComplete={onAnimationComplete}
      />
    );
  }

  // CSS-переменная --overlap управляет отрицательным margin между шашками
  const wrapperStyle: React.CSSProperties =
    overlapPx > 0 ? ({ "--overlap": `-${overlapPx}px` } as React.CSSProperties) : {};

  return (
    <div
      className={cx("Point-wrapper", topOrBottomClass)}
      onClick={() => {
        const result = clickHandler(pointNumber);
        if (!result) {
          setShowNoMoveHighlight(true);
          setTimeout(() => {
            setShowNoMoveHighlight(false);
          }, 1000);
        }
      }}
    >
      <div ref={wrapperRef} className={cx("Checkers-wrapper", topOrBottomClass)} style={wrapperStyle}>{checkers}</div>
      {highlight}
      <div
        className={cx("Point-triangle-inner", {
          ...topOrBottomClass,
          even: pointNumber % 2 === 0,
          odd: pointNumber % 2 !== 0,
        })}
      />
      <div
        className={cx("Point-triangle", {
          ...topOrBottomClass,
          even: pointNumber % 2 === 0,
          odd: pointNumber % 2 !== 0,
        })}
      />
    </div>
  );
};

export default BoardPoint;
